<template>
  <div class="ak-sidebar-layout" :class="classes">
    <div
      v-if="sidebarRight"
      class="ak-sidebar-layout__body"
      :class="sidebarBodyClasses"
    >
      <slot name="body" />
    </div>
    <div class="ak-sidebar-layout__sidebar" :class="sidebarClasses">
      <div
        class="ak-sidebar-layout__toggle"
        :class="[sidebarColor, sidebarToggleColor]"
        @click="openSidebar"
      >
        <ak-icon
          class="ak-sidebar-layout__toggle-icon"
          :type="sidebarRight ? 'chevron-left' : 'chevron-right'"
        />
      </div>
      <div class="ak-sidebar-layout__sidebar-inner">
        <ak-sidebar :color="sidebarStyle">
          <slot name="sidebar" />
        </ak-sidebar>
      </div>
    </div>
    <div
      v-if="!sidebarRight"
      class="ak-sidebar-layout__body"
      :class="sidebarBodyClasses"
    >
      <slot name="body" />
    </div>
  </div>
</template>

<script>
import { computed, ref, defineComponent } from 'vue'
import Sidebar from './Sidebar.vue'
import Icon from '../icon/Icon.vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-sidebar-layout>
   *    <ak-sidebar>Sidebar</ak-sidebar>
   *    <div class="flex__item">Content</div>
   * </ak-sidebar-layout>
   */
  name: 'AkSidebarLayout',

  components: {
    [Sidebar.name]: Sidebar,
    [Icon.name]: Icon,
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Boolean}
     * @description Determines of the sidebar should be on the right
     */
    sidebarRight: {
      type: Boolean,
      default: false,
    },
    /**
     * @type {String}
     * @description Controls the size of the sidebar
     */
    size: {
      type: String,
      default: null,
    },

    /**
     * @type {String}
     * @description Controls the bg of the sidebar
     */
    sidebarStyle: {
      type: String,
      default: 'default',
      validator: (o) => ['light', 'dark', 'default'].indexOf(o) >= 0,
    },

    /**
     * @type {String}
     * @description Controls whether the sidebar stacks on palm size devices
     */
    sidebarPalmStack: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {String}
     * @description Controls whether the sidebar stacks on portable size devices
     */
    sidebarPortableStack: {
      type: Boolean,
      default: false,
    },
    /**
     * @type {String}
     * @description Adds .overflow-hidden to layout body to allow horizontal scrolling
     */
    overflowHidden: {
      type: Boolean,
      default: false,
    },
  },

  /**
   * @param {Object} props - The properties of the component
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props) {
    const sidebarOpen = ref(false)

    const classes = computed(() => {
      return {
        'ak-sidebar-layout--palm-stacks': props.sidebarPalmStack,
        'ak-sidebar-layout--portable-stacks': props.sidebarPortableStack,
      }
    })

    const sidebarBodyClasses = computed(() => {
      return {
        'overflow-hidden': props.overflowHidden,
      }
    })

    const sidebarClasses = computed(() => {
      return {
        [`ak-sidebar-layout__sidebar--${props.size}`]: props.size,
        'ak-sidebar-layout__sidebar--right': props.sidebarRight,
        'is-open': sidebarOpen.value,
      }
    })

    const sidebarColor = computed(() => {
      switch (props.sidebarStyle) {
        case 'light':
          return 'color-grey-dark'
        case 'dark':
          return 'color-white'
        default:
          return 'color-text'
      }
    })

    const sidebarToggleColor = computed(() => {
      switch (props.sidebarStyle) {
        case 'light':
          return 'bg-white'
        case 'dark':
          return 'bg-grey-dark'
        default:
          return 'bg-grey-lightest'
      }
    })

    const openSidebar = (e) => {
      /* istanbul ignore if */
      if (e && e.originalEvent instanceof Event) {
        e.preventDefault()
        e.stopPropagation()
      }

      sidebarOpen.value = !sidebarOpen.value
    }

    return {
      sidebarToggleColor,
      sidebarClasses,
      sidebarColor,
      sidebarOpen,
      sidebarBodyClasses,
      openSidebar,
      classes,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
.ak-sidebar-layout {
  $self: &;
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
  flex-grow: 1;
  &--portable-stacks {
    @media screen and #{$mq-portable} {
      display: block;
      overflow: auto;
    }
  }
  &--palm-stacks {
    @media screen and #{$mq-palm} {
      display: block;
      overflow: auto;
    }
  }

  &__body {
    flex-grow: 1;
    flex-basis: 0;
    display: flex;
    flex-direction: column;

    @media screen and #{$mq-desk} {
      + #{$self}__sidebar {
        border-left: $base-border;
      }
    }
  }

  &__sidebar {
    flex-shrink: 1;
    flex-basis: $sidebar-width;
    width: $sidebar-width;
    position: relative;

    &-inner {
      height: 100%;
      overflow-y: auto;
    }

    &--tiny {
      flex-basis: $sidebar-width * 0.5;
      width: $sidebar-width * 0.5;
    }
    &--small {
      flex-basis: $sidebar-width * 0.75;
      width: $sidebar-width * 0.75;
    }
    &--large {
      flex-basis: $sidebar-width * 1.25;
      width: $sidebar-width * 1.25;
    }
    &--huge {
      flex-basis: $sidebar-width * 1.5;
      width: $sidebar-width * 1.5;
    }
    @media screen and #{$mq-desk} {
      + #{$self}__body {
        border-left: $base-border;
      }
    }

    @media screen and #{$mq-portable} {
      position: absolute;
      z-index: 20;
      left: 0;
      bottom: 0;
      top: 0;
      bottom: 0;
      width: $sidebar-width * 1.25;
      height: 100%;
      background: #fff;
      transform: translateX(-100%);
      transition: all $transition-time $ease-out;

      &-inner {
        height: 100%;
        overflow-y: auto;
      }

      &.is-open {
        transform: translateX(0);
        z-index: 99;
        box-shadow: $base-shadow-large;
      }
      &--right {
        left: auto;
        right: 0;
        transform: translateX(100%);
        &.is-open {
          transform: translateX(0);
        }
      }
    }
  }

  &__toggle {
    position: absolute;
    width: 1.5rem;
    height: 2.5rem;
    top: calc(50% - 1.25rem);
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 0 $base-border-radius $base-border-radius 0;
    border: $base-border;
    border-left: 0;
    background-color: #fff;
    left: 100%;
    z-index: 20;
    box-shadow: $base-shadow;

    &-icon {
      transition: $transition;
      .is-open & {
        transform: rotate(180deg);
      }
    }

    #{$self}__sidebar--right & {
      right: 100%;
      left: auto;
      border: $base-border;
      border-right: 0;
      border-radius: $base-border-radius 0 0 $base-border-radius;
    }

    @media screen and #{$mq-desk} {
      display: none;
    }
  }
}
</style>
